import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import Swal from "sweetalert2";
import { Link } from 'react-router-dom';
import { API_URL } from '../../api/api';

const API_BRANCH = `${API_URL}/branches`;
const token = localStorage.getItem('token');

const ManageEnquiryLayer = () => {
    const [showForwardModal, setShowForwardModal] = useState(false);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [selectedBranchId, setSelectedBranchId] = useState('');
    const [branchSearch, setBranchSearch] = useState('');
    const [enquiries, setEnquiries] = useState([]);
    const [branches, setBranches] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false); // Confirmation modal for delete
    const [editEnquiry, setEditEnquiry] = useState(null);
    const [deleteEnquiryId, setDeleteEnquiryId] = useState(null); // Store ID of the enquiry to delete
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        type: '',
        status: '',
    });
    const [currentPage, setCurrentPage] = useState(1); // For tracking the current page
    const [totalPages, setTotalPages] = useState(1);  // For tracking the total pages
    // Fetch enquiries from API with pagination
    useEffect(() => {
        fetchBranches();
        fetchEnquiries();
        axios.get(`${API_URL}/adminenquiries?page=${currentPage}`)
            .then(response => {
                setEnquiries(response.data.data); // Set the enquiries data
                setTotalPages(response.data.last_page); // Set total pages
            })
            .catch(error => {
                console.error("There was an error fetching enquiries!", error);
            });
    }, [currentPage]); // Fetch data whenever the page changes

    const fetchEnquiries = () => {
        axios.get(`${API_URL}/adminenquiries?page=${currentPage}`)
            .then(response => {
                setEnquiries(response.data.data);
                setTotalPages(response.data.last_page);
            })
            .catch(error => {
                console.error("There was an error fetching enquiries!", error);
            });
    };

    // Function to fetch branches
    const fetchBranches = async () => {
        try {
            const response = await axios.get(API_BRANCH, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const branchList = Array.isArray(response.data)
                ? response.data
                : response.data.data; // safest fallback

            setBranches(branchList);
        } catch (error) {
            console.error("Error fetching branches", error);
        }
    };


    const handleForwardConfirm = () => {
        const selectedBranch = branches.find(branch => branch.id === selectedBranchId);
        const branchName = selectedBranch?.centre_name || `ID ${selectedBranchId}`;

        Swal.fire({
            title: 'Are you sure?',
            text: `Forward enquiry to branch: ${branchName}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, forward it!',
        }).then((result) => {
            if (result.isConfirmed) {
                axios.post(`${API_URL}/enquiries/${selectedEnquiry.id}/forward`, {
                    branch_id: selectedBranchId
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }).then(() => {
                    Swal.fire('Forwarded!', 'Enquiry has been forwarded.', 'success');
                    setShowForwardModal(false);
                    setSelectedBranchId('');
                    setSelectedEnquiry(null);
                    fetchEnquiries(); // 🔁 refresh table here
                }).catch(error => {
                    console.error('Error forwarding enquiry:', error);
                    Swal.fire('Error', 'Failed to forward the enquiry.', 'error');
                });
            }
        });
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    // Add Enquiry
    const handleAddEnquiry = (e) => {
        e.preventDefault();
        axios.post(`${API_URL}/enquiries`, formData, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
            .then(response => {
                setEnquiries([...enquiries, response.data]);
                setShowAddModal(false);
                setFormData({ name: '', email: '', phone: '', message: '', type: '', status: '' });
            })
            .catch(error => {
                console.error("There was an error adding the enquiry!", error);
            });
    };

    // Edit Enquiry
    const handleEditEnquiry = (e) => {
        e.preventDefault();
        axios.put(`${API_URL}/enquiries/${editEnquiry.id}`, formData)
            .then(response => {
                const updatedEnquiries = enquiries.map(enquiry =>
                    enquiry.id === editEnquiry.id ? response.data : enquiry
                );
                setEnquiries(updatedEnquiries);
                setShowEditModal(false);
                setFormData({ name: '', email: '', phone: '', message: '', type: '', status: '' });
            })
            .catch(error => {
                console.error("There was an error updating the enquiry!", error);
            });
    };

    // Delete Enquiry - This function will be triggered after confirmation
    const handleDeleteEnquiry = () => {
        axios.delete(`${API_URL}/enquiries/${deleteEnquiryId}`)
            .then(() => {
                const updatedEnquiries = enquiries.filter(enquiry => enquiry.id !== deleteEnquiryId);
                setEnquiries(updatedEnquiries);
                setShowDeleteModal(false); // Close delete confirmation modal
            })
            .catch(error => {
                console.error("There was an error deleting the enquiry!", error);
                setShowDeleteModal(false); // Close delete modal if error occurs
            });
    };

    // Open edit modal with data
    const openEditModal = (enquiry) => {
        setEditEnquiry(enquiry);
        setFormData({
            name: enquiry.name,
            email: enquiry.email,
            phone: enquiry.phone,
            message: enquiry.message,
            type: enquiry.type,
            status: enquiry.status,
        });
        setShowEditModal(true);
    };

    // Open delete confirmation modal
    const openDeleteModal = (id) => {
        setDeleteEnquiryId(id);
        setShowDeleteModal(true);
    };

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <div className="card">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                <p className="mb-0">Manage Enquiry</p>
                <Link onClick={() => setShowAddModal(true)} className="btn btn-sm btn-primary-600">
                    <i className="ri-add-line" /> Add Enquiry
                </Link>
            </div>
            <div className="card-body">
                <table className="table table-bordered mb-0">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Name</th>
                            {/* <th>Email</th> */}
                            <th>Phone</th>
                            <th>Type</th>
                            <th>Message</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {enquiries.length > 0 ? (
                            [...enquiries]
                                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                .map((enquiry, index) => (

                                    <tr key={enquiry.id}>
                                        <td>{index + 1}</td>
                                        <td>{enquiry.name}</td>
                                        {/* <td>{enquiry.email}</td> */}
                                        <td>{enquiry.phone}</td>
                                        <td>{enquiry.type}</td>
                                        <td>{enquiry.message}</td>
                                        <td>
                                            <span className={`badge ${enquiry.status === 'active' ? 'bg-success' :
                                                enquiry.status === 'pending' ? 'bg-warning text-dark' :
                                                    enquiry.status === 'in_progress' ? 'bg-info text-dark' :
                                                        enquiry.status === 'resolved' ? 'bg-primary' :
                                                            'bg-secondary'
                                                }`}>
                                                {enquiry.status ? enquiry.status.replace('_', ' ').toUpperCase() : 'N/A'}
                                            </span>
                                        </td>


                                        <td>{new Date(enquiry.created_at).toLocaleString()}</td>
                                        <td>

                                            <Link
                                                onClick={() => {
                                                    setSelectedEnquiry(enquiry);
                                                    setShowForwardModal(true);
                                                }}
                                                className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                            >
                                                <Icon icon="majesticons:eye-line" className="icon text-xl" />
                                            </Link>
                                            <Link
                                                onClick={() => openEditModal(enquiry)}
                                                className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                title="Edit"
                                            >
                                                <Icon icon="lucide:edit" />
                                            </Link>
                                            <Link
                                                className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                onClick={() => openDeleteModal(enquiry.id)}
                                                title="Delete"
                                            >
                                                <Icon icon="mingcute:delete-2-line" />
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center">No enquiries found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {/* Pagination Controls */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <span>Page {currentPage} of {totalPages}</span>
                    <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 && 'disabled'}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                &laquo;
                            </button>
                        </li>
                        {[...Array(totalPages)].map((_, i) => (
                            <li key={i} className={`page-item ${currentPage === i + 1 && 'active'}`}>
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(i + 1)}
                                >
                                    {i + 1}
                                </button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages && 'disabled'}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                &raquo;
                            </button>
                        </li>
                    </ul>
                </div>


                {/* Add Enquiry Modal */}
                <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
                    <Modal.Header closeButton>
                        <h6>Add Enquiry</h6>
                    </Modal.Header>
                    <Modal.Body>
                        <form onSubmit={handleAddEnquiry}>
                            <div className="mb-3">
                                <label className="form-label">Name</label>
                                <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Email</label>
                                <input type="email" className="form-control" name="email" value={formData.email} onChange={handleChange} required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Phone</label>
                                <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleChange} required />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Type</label>
                                <select className="form-control" name="type" value={formData.type} onChange={handleChange} required>
                                    <option>Select Type</option>
                                    <option>Admission</option>
                                    <option>Exam</option>
                                    <option>Fee</option>
                                    <option>Result</option>
                                    <option>Student</option>
                                    <option>Parent</option>
                                    <option>Others</option>
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Message</label>
                                <textarea className="form-control" name="message" value={formData.message} onChange={handleChange} required></textarea>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Status</label>
                                <select className="form-control" name="status" value={formData.status} onChange={handleChange} required>
                                    <option value="">Select Status</option>
                                    <option value="active">Active</option>
                                    <option value="pending">Pending</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="resolved">Resolved</option>
                                </select>
                            </div>

                            <Button type="submit" variant="success">Save Enquiry</Button>
                        </form>
                    </Modal.Body>
                </Modal>

                {/* Edit Enquiry Modal */}
                <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                    <Modal.Header closeButton>
                        <h6>Edit Enquiry</h6>
                    </Modal.Header>
                    <Modal.Body>
                        <form onSubmit={handleEditEnquiry}>
                            <div className="mb-3">
                                <label className="form-label">Name</label>
                                <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Email</label>
                                <input type="email" className="form-control" name="email" value={formData.email} onChange={handleChange} required />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Type</label>
                                <select className="form-control" name="type" value={formData.type} onChange={handleChange} required>
                                    <option>Select Type</option>
                                    <option>Admission</option>
                                    <option>Exam</option>
                                    <option>Fee</option>
                                    <option>Result</option>
                                    <option>Student</option>
                                    <option>Parent</option>
                                    <option>Others</option>
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Phone</label>
                                <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleChange} required />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Message</label>
                                <textarea className="form-control" name="message" value={formData.message} onChange={handleChange} required></textarea>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Status</label>
                                <select className="form-control" name="status" value={formData.status} onChange={handleChange} required>
                                    <option value="">Select Status</option>
                                    <option value="active">Active</option>
                                    <option value="pending">Pending</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="resolved">Resolved</option>
                                </select>
                            </div>
                            <Button type="submit" variant="primary">Update Enquiry</Button>
                        </form>
                    </Modal.Body>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Deletion</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete this enquiry?</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDeleteEnquiry}>
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={showForwardModal} onHide={() => setShowForwardModal(false)}>
                    <Modal.Header closeButton>
                        <h6>Forward Enquiry to Branch</h6>
                    </Modal.Header>
                    <Modal.Body>
                        <input
                            type="text"
                            className="form-control mb-3"
                            placeholder="Search by name, state, district, pincode, or mobile..."
                            value={branchSearch}
                            onChange={(e) => setBranchSearch(e.target.value)}
                        />

                        <div className="list-group" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {branches
                                .filter(branch => {
                                    const query = branchSearch.toLowerCase();
                                    return (
                                        branch?.centre_name?.toLowerCase().includes(query) ||
                                        branch?.state?.toLowerCase().includes(query) ||
                                        branch?.district?.toLowerCase().includes(query) ||
                                        branch?.pin_code?.toString().includes(query) ||
                                        branch?.mobile_no?.toLowerCase().includes(query) ||
                                        branch?.login_mobile?.toLowerCase().includes(query)
                                    );
                                })
                                .map(branch => (
                                    <button
                                        key={branch.id}
                                        className={`list-group-item list-group-item-action ${selectedBranchId === branch.id ? 'active' : ''}`}
                                        onClick={() => setSelectedBranchId(branch.id)}
                                    >
                                        <div><strong>{branch.centre_name}</strong></div>
                                        <div className="small text-muted">
                                            {branch.state}, {branch.district}, {branch.pin_code} | {branch.mobile_no}
                                        </div>
                                    </button>
                                ))
                            }

                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowForwardModal(false)}>Cancel</Button>
                        <Button
                            variant="primary"
                            disabled={!selectedBranchId}
                            onClick={() => handleForwardConfirm()}
                        >
                            Forward
                        </Button>
                    </Modal.Footer>
                </Modal>

            </div>
        </div>
    );
};

export default ManageEnquiryLayer;
